import { reportInfo } from '@ms/yammer-telemetry';
import { getMetaOsHubContextThrows } from '@ms/yammer-web-support/dist/metaOsHub';
import type { TeamsContext } from 'state/teams';

const supportedThemes = ['default', 'dark', 'contrast'];

const isThemeSupported = (teamsTheme: string): teamsTheme is TeamsContext['theme'] =>
  supportedThemes.includes(teamsTheme);

const getSupportedTheme = (theme?: string): TeamsContext['theme'] =>
  theme && isThemeSupported(theme) ? theme : 'default';

type GetContext = () => Promise<TeamsContext>;
export const getContext: GetContext = async () => {
  const { app, chat, meeting, page, user } = await getMetaOsHubContextThrows();

  return {
    theme: getSupportedTheme(app.theme),
    locale: app.locale,
    hostClientType: app.host.clientType,
    hostAppName: app.host.name,
    subEntityId: page.subPageId,
    sessionId: app.host.sessionId,
    appSessionId: app.sessionId,
    ringId: app.host.ringId,
    meetingId: meeting?.id,
    userId: user?.id,
    licenseType: user?.licenseType,
    userTenantId: user?.tenant?.id,
    frameContext: page.frameContext,
    userClickTime: app.userClickTime,
    chatId: chat?.id,
    isBackgroundLoad: page.isBackgroundLoad,
  };
};

type GetContextWithTelemetry = () => Promise<TeamsContext>;
export const getContextWithTelemetry: GetContextWithTelemetry = async () => {
  const teamsContext = await getContext();
  const { sessionId, appSessionId, ringId, locale, hostAppName, meetingId } = teamsContext;

  reportInfo({
    eventName: 'teams_client_metadata',
    eventProperties: {
      teamsClientMetadata: `sid:${sessionId}::asid:${appSessionId}::rid:${ringId}::loc:${locale}::han:${hostAppName}::mid:${meetingId}`,
    },
  });

  return teamsContext;
};

type IsAnonymousUser = () => Promise<boolean>;
export const isAnonymousUser: IsAnonymousUser = async () => {
  try {
    const { licenseType } = await getContext();

    return licenseType === 'Anonymous';
  } catch {
    return false;
  }
};
